import {
  Button,
  Fieldset,
  Grid,
  Group,
  NumberInput,
  Radio,
  Select,
  Space,
  TagsInput,
  TextInput,
  Transition,
} from '@mantine/core'
import EnterpriseCheckoutsFormViewModel from 'components/enterpriseCheckouts/EnterpriseCheckoutsFormViewModel'
import CouponInput from 'components/ui/couponInput/CouponInput'
import { getNames } from 'i18n-iso-countries'
import { observer } from 'mobx-react-lite'

type EnterpriseCheckoutsFormProps = {
  viewModel: EnterpriseCheckoutsFormViewModel
  onSuccess?: () => void
}

export default observer(function EnterpriseCheckoutsForm(
  props: EnterpriseCheckoutsFormProps,
) {
  return (
    <>
      <Fieldset legend="Lead information">
        <Grid gutter="sm">
          <Grid.Col span={6}>
            <TextInput
              label="First name"
              value={props.viewModel.firstName}
              onChange={(e) => props.viewModel.setFirstName(e.target.value)}
              error={props.viewModel.firstNameError}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label="Last name"
              value={props.viewModel.lastName}
              onChange={(e) => props.viewModel.setLastName(e.target.value)}
              error={props.viewModel.lastNameError}
            />
          </Grid.Col>
        </Grid>
        <Grid gutter="sm">
          <Grid.Col span={6}>
            <TextInput
              label="Job position"
              value={props.viewModel.jobPosition}
              onChange={(e) => props.viewModel.setJobPosition(e.target.value)}
              error={props.viewModel.jobPositionError}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label="Email"
              value={props.viewModel.email}
              onChange={(e) => props.viewModel.setEmail(e.target.value)}
              error={props.viewModel.emailError}
            />
          </Grid.Col>
        </Grid>
      </Fieldset>
      <Space h="xl" />
      <Fieldset legend="Plan information">
        <Grid gutter="sm">
          <Grid.Col span={6}>
            <Radio.Group
              label="Plan"
              value={props.viewModel.plan}
              onChange={handlePlanChange}
              error={props.viewModel.planError}
            >
              <Group mt="xs">
                <Radio value="enterprise" label="Enterprise" />
                <Radio value="business" label="Business" />
              </Group>
            </Radio.Group>
          </Grid.Col>
          <Grid.Col span={6}>
            <Radio.Group
              label="Billing interval"
              value={props.viewModel.billingInterval}
              onChange={handleBillingIntervalChange}
              error={props.viewModel.billingIntervalError}
            >
              <Group mt="xs">
                <Radio value="yearly" label="Yearly" />
                <Radio value="monthly" label="Monthly" />
              </Group>
            </Radio.Group>
          </Grid.Col>
        </Grid>
        <Space h="sm" />
        <Grid gutter="sm">
          <Grid.Col span={6}>
            <Radio.Group
              label="Currency"
              value={props.viewModel.currencyCode}
              onChange={handleCurrencyCodeChange}
              error={props.viewModel.currencyCodeError}
            >
              <Group mt="xs">
                <Radio value="USD" label="USD" />
                <Radio value="EUR" label="EUR" />
              </Group>
            </Radio.Group>
          </Grid.Col>
          <Grid.Col span={6}>
            <NumberInput
              label="Seats count"
              value={props.viewModel.seatCount}
              onChange={(value) =>
                props.viewModel.setSeatCount(value ? parseInt(`${value}`) : 0)
              }
              error={props.viewModel.seatCountError}
            />
          </Grid.Col>
        </Grid>
      </Fieldset>
      <Space h="xl" />
      <Fieldset legend="Deduction">
        <Grid>
          <Grid.Col span={6}>
            <CouponInput
              disabled={props.viewModel.couponsLoading}
              value={props.viewModel.stripeCouponId}
              onChange={props.viewModel.setStripeCouponId}
              error={props.viewModel.stripeCouponIdError}
            />
          </Grid.Col>
          {!!props.viewModel.stripeCouponId && (
            <Grid.Col span={6}>
              <TextInput
                label="Promotion code"
                value={props.viewModel.promotionCodeCode}
                onChange={(e) =>
                  props.viewModel.setPromotionCodeCode(e.target.value)
                }
                error={props.viewModel.promotionCodeCodeError}
              />
            </Grid.Col>
          )}
        </Grid>
      </Fieldset>
      <Space h="xl" />
      <Fieldset legend="Company information">
        <Grid>
          <Grid.Col span={6}>
            <TextInput
              label="Business name"
              value={props.viewModel.businessName}
              onChange={(e) => props.viewModel.setBusinessName(e.target.value)}
              error={props.viewModel.businessNameError}
            />
          </Grid.Col>
        </Grid>
        <Grid>
          <Grid.Col span={6}>
            <TextInput
              label="Address line 1"
              value={props.viewModel.line1}
              onChange={(e) => props.viewModel.setLine1(e.target.value)}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label="Address line 2"
              value={props.viewModel.line2}
              onChange={(e) => props.viewModel.setLine2(e.target.value)}
            />
          </Grid.Col>
        </Grid>
        <Grid>
          <Grid.Col span={6}>
            <TextInput
              label="Postal code"
              value={props.viewModel.postalCode}
              onChange={(e) => props.viewModel.setPostalCode(e.target.value)}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label="City"
              value={props.viewModel.city}
              onChange={(e) => props.viewModel.setCity(e.target.value)}
            />
          </Grid.Col>
        </Grid>
        <Grid>
          <Grid.Col span={6}>
            <TextInput
              label="State / Province"
              value={props.viewModel.state}
              onChange={(e) => props.viewModel.setState(e.target.value)}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <Select
              label="Country"
              value={props.viewModel.country}
              onChange={(value) => props.viewModel.setCountry(value)}
              data={countryOptions()}
              searchable
            />
          </Grid.Col>
        </Grid>
        <Grid>
          <Grid.Col span={6}>
            <TagsInput
              data={[]}
              value={props.viewModel.emails}
              onChange={props.viewModel.setEmails}
              label="Billing emails"
              placeholder={'Enter emails separated by commas'}
              error={props.viewModel.emailsError}
            />
          </Grid.Col>
        </Grid>
      </Fieldset>
      <Group mt="xl">
        <Button onClick={handleSave} loading={props.viewModel.loading}>
          {props.viewModel.enterpriseCheckout?.persisted
            ? 'Mettre à jour'
            : 'Créer'}
        </Button>
      </Group>
    </>
  )

  function handleSave() {
    props.viewModel.save(props.onSuccess)
  }

  function handlePlanChange(value: string) {
    if (value === 'enterprise' || value === 'business') {
      props.viewModel.setPlan(value)
    }
  }

  function handleBillingIntervalChange(value: string) {
    if (value === 'yearly' || value === 'monthly') {
      props.viewModel.setBillingInterval(value)
    }
  }

  function handleCurrencyCodeChange(value: string) {
    if (value === 'USD' || value === 'EUR') {
      props.viewModel.setCurrencyCode(value)
    }
  }

  function countryOptions() {
    return Object.entries(
      getNames('en', {
        select: 'official',
      }),
    ).map((countryCode) => ({
      value: countryCode[0],
      label: countryCode[1],
    }))
  }
})
